// A globális kereső találat-típusainak KLIENS-OLDALI feloldása: `type` → (1) navigációs cél (route/sheet
// deep-link), (2) típus-ikon, (3) i18n csoport-címke kulcs. A BFF routing-agnosztikus (`{type,id,label,sublabel}`),
// a cél-feloldás ITT él (a command palette ezt használja). A detail-oldalak `$id`-route-ra, a sheet-alapú oldalak
// `?edit=`/`?open=` deep-linkre, az org-struktúra `?tab=…&edit=` linkre visznek — a route-fák konvenciói szerint.
import { type GlobalSearchResult } from '@atlas/contracts';
import {
  ArrowTrendingDownIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  CubeIcon,
  FlagIcon,
  IdentificationIcon,
  LifebuoyIcon,
  NewspaperIcon,
  QueueListIcon,
  ReceiptPercentIcon,
  RectangleGroupIcon,
  Square3Stack3DIcon,
  TicketIcon,
  UserCircleIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import type { useNavigate } from '@tanstack/react-router';
import { type ComponentType, type SVGProps } from 'react';

export type SearchResultType = GlobalSearchResult['type'];
type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

// A REGISZTRÁLT route-fa tipizált navigate-fn-je. A cél-feloldást a helper VÉGZI (nem objektumot ad vissza):
// így minden ág egy LITERÁL navigate-hívás, amit a TanStack a `to`-literálból helyesen szűkít (nincs `as`, nincs any).
type TypedNavigate = ReturnType<typeof useNavigate>;

/**
 * Egy találat `type`-ja + `id`-ja → a hozzá tartozó navigáció. A detail-route-ok `$id`-paramot kapnak, a
 * sheet-alapú oldalak a deep-link search-paramot (`edit`/`open`), az org-struktúra a fül + `edit` párost.
 * Best-effort: a `meeting`/`asset` a listát nyitja (ha a range-en/fülön kívül esik) — ez elfogadott.
 */
export function navigateToSearchResult(
  navigate: TypedNavigate,
  type: SearchResultType,
  id: string,
): void {
  switch (type) {
    case 'client':
      void navigate({ to: '/clients/$id', params: { id } });
      return;
    case 'account':
      void navigate({ to: '/accounts/$id', params: { id } });
      return;
    case 'ticket':
      void navigate({ to: '/tickets/$id', params: { id } });
      return;
    case 'invoice':
      void navigate({ to: '/invoices/$id', params: { id } });
      return;
    // A KIADÁSNAK nincs detail-lapja és nincs sor-deep-linkje: a Pénzügy lap Kiadások fülét nyitjuk meg —
    // ugyanaz a „best-effort" megállapodás, mint a meeting/asset találatnál (a lista nyílik, nem a sor).
    case 'expense':
      void navigate({ to: '/finance', search: { tab: 'expenses' } });
      return;
    case 'contact':
      void navigate({ to: '/contacts', search: { edit: id } });
      return;
    case 'employee':
      void navigate({ to: '/employees', search: { edit: id } });
      return;
    case 'supportTeam':
      void navigate({ to: '/support-teams', search: { edit: id } });
      return;
    case 'serviceAssignment':
      void navigate({ to: '/services', search: { tab: 'assignments', asg_edit: id } });
      return;
    case 'serviceCatalog':
      void navigate({ to: '/services', search: { tab: 'catalog', cat_edit: id } });
      return;
    case 'message':
      void navigate({ to: '/messages', search: { open: id } });
      return;
    case 'meeting':
      void navigate({ to: '/meetings', search: { open: id } });
      return;
    case 'asset':
      void navigate({ to: '/assets', search: { open: id } });
      return;
    case 'campaign':
      void navigate({ to: '/campaign-forecast', search: { edit: id } });
      return;
    case 'productUpdate':
      void navigate({ to: '/product-updates', search: { edit: id } });
      return;
    case 'department':
      void navigate({ to: '/org-structure', search: { tab: 'departments', edit: id } });
      return;
    case 'team':
      void navigate({ to: '/org-structure', search: { tab: 'teams', edit: id } });
      return;
    case 'position':
      void navigate({ to: '/org-structure', search: { tab: 'positions', edit: id } });
      return;
    default: {
      // Exhaustiveness-guard: új GLOBAL_SEARCH_TYPES-elem itt FORDÍTÁSI hibát ad (nem néma no-op).
      const _exhaustive: never = type;
      void _exhaustive;
    }
  }
}

/** Típus → ikon (a sidebar/nav ikonjaival összhangolva). */
export const SEARCH_TYPE_ICONS: Record<SearchResultType, IconComponent> = {
  client: BriefcaseIcon,
  account: BuildingOffice2Icon,
  contact: UserCircleIcon,
  ticket: TicketIcon,
  invoice: ReceiptPercentIcon,
  expense: ArrowTrendingDownIcon,
  message: ChatBubbleLeftRightIcon,
  meeting: CalendarIcon,
  employee: UsersIcon,
  supportTeam: LifebuoyIcon,
  asset: CubeIcon,
  serviceAssignment: Square3Stack3DIcon,
  serviceCatalog: QueueListIcon,
  // a kampány a lap saját nav-ikonját viseli; a fejlesztés a VÁLTOZÁSNAPLÓ newspaper-ikonját (nem a megafont
  // — az a modul menüpontjáé, l. `product-updates.md`)
  campaign: FlagIcon,
  productUpdate: NewspaperIcon,
  department: RectangleGroupIcon,
  team: UserGroupIcon,
  position: IdentificationIcon,
};

/** Típus → a csoport-fejléc i18n-kulcsa (kétnyelvű megjelenítés a palette-ben). */
export const SEARCH_TYPE_LABEL_KEYS: Record<SearchResultType, string> = {
  client: 'nav.resultTypes.client',
  account: 'nav.resultTypes.account',
  contact: 'nav.resultTypes.contact',
  ticket: 'nav.resultTypes.ticket',
  invoice: 'nav.resultTypes.invoice',
  expense: 'nav.resultTypes.expense',
  message: 'nav.resultTypes.message',
  meeting: 'nav.resultTypes.meeting',
  employee: 'nav.resultTypes.employee',
  supportTeam: 'nav.resultTypes.supportTeam',
  asset: 'nav.resultTypes.asset',
  serviceAssignment: 'nav.resultTypes.serviceAssignment',
  serviceCatalog: 'nav.resultTypes.serviceCatalog',
  campaign: 'nav.resultTypes.campaign',
  productUpdate: 'nav.resultTypes.productUpdate',
  department: 'nav.resultTypes.department',
  team: 'nav.resultTypes.team',
  position: 'nav.resultTypes.position',
};
